
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import AuthProvider from "@/providers/AuthProvider";
import Navbar from "@/components/Navbar";
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import Signup from "./pages/Signup";
import Landing from "./pages/Landing";
import Privacy from "./pages/Privacy";
import DeleteAccount from "./pages/DeleteAccount";

import Profile from "./pages/Profile";
import Help from "./pages/Help";
import PaymentSettings from "./pages/PaymentSettings";
import ReviewLinks from "./pages/ReviewLinks";
import Subscription from "./pages/Subscription";

import Customers from "./pages/Customers";

import NotFound from "./pages/NotFound";
import { useEffect, useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/providers/AuthProvider";
import SplashScreen from "@/components/SplashScreen";
import { hidePaywallUI } from "@/lib/platform";

// Import Capacitor plugins
import { App as CapacitorApp } from '@capacitor/app';
import { SplashScreen as CapacitorSplashScreen } from '@capacitor/splash-screen';

import { useRealtimeSync } from "@/hooks/useRealtimeSync";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      staleTime: 30_000,
    },
  },
});

// Mounted inside providers so it can use both auth + react-query context
const RealtimeSyncMount = () => {
  useRealtimeSync();
  return null;
};

// Conditionally render navbar based on route and auth state
const NavbarWrapper = () => {
  const location = useLocation();
  const { user } = useAuth();

  // Never show navbar on landing/auth/signup pages
  const noNavbarPaths = ["/", "/home", "/auth", "/signup"];
  if (noNavbarPaths.includes(location.pathname)) return null;

  // On help page, only show navbar when logged in
  if (location.pathname === "/help" && !user) return null;

  return <Navbar />;
};

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// This component handles root routing based on authentication status
const RootRedirect = () => {
  const { user } = useAuth();

  // If user is logged in, redirect to dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  // On iOS, skip the marketing/pricing landing page — Apple's IAP rules
  // forbid showing external paywalls in the app. Send straight to sign-in.
  if (hidePaywallUI()) {
    return <Navigate to="/auth" replace />;
  }

  // Public marketing page for unauthenticated visitors
  return <Landing />;
};

const App = () => {
  // Detect if running in native Capacitor environment
  const isNative = window.hasOwnProperty('Capacitor');
  const isMobile = useIsMobile();
  // Show splash only once per tab (fresh load / new tab), and again after a fresh sign-in.
  const [showSplash, setShowSplash] = useState(() => {
    try {
      return sessionStorage.getItem("splashShown") !== "1";
    } catch {
      return true;
    }
  });

  const dismissSplash = () => {
    try { sessionStorage.setItem("splashShown", "1"); } catch {}
    setShowSplash(false);
  };

  useEffect(() => {
    // Only re-show splash on a true fresh sign-in (user id changes from none -> some).
    let lastUserId: string | null = null;
    let initialized = false;
    let unsub: (() => void) | undefined;
    import("@/integrations/supabase/client").then(({ supabase }) => {
      supabase.auth.getSession().then(({ data: { session } }) => {
        lastUserId = session?.user?.id ?? null;
        initialized = true;
      });
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
        const newId = session?.user?.id ?? null;
        if (initialized && event === "SIGNED_IN" && newId && newId !== lastUserId) {
          try { sessionStorage.removeItem("splashShown"); } catch {}
          setShowSplash(true);
        }
        lastUserId = newId;
      });
      unsub = () => subscription.unsubscribe();
    });
    return () => unsub?.();
  }, []);

  useEffect(() => {
    // Initialize native plugins
    if (isNative) {
      try {
        // Hide native splash screen - we'll use our React splash screen instead
        CapacitorSplashScreen.hide();
        
        // Handle back button for Android
        CapacitorApp.addListener('backButton', ({ canGoBack }) => {
          if (!canGoBack) {
            CapacitorApp.exitApp();
          } else {
            window.history.back();
          }
        });
        
      } catch (error) {
        console.error("Error initializing native plugins:", error);
      }
    }
    
    // Apply the mint theme throughout the application
    document.documentElement.classList.add('mint-theme');
  }, [isNative]);

  return (
    <>
      {showSplash && <SplashScreen onFinish={dismissSplash} />}
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <RealtimeSyncMount />
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <div className="flex min-h-dvh w-full max-w-full flex-col">
              <NavbarWrapper />
              
              <main className={`flex-grow pb-0 ${isNative ? 'pt-safe-top pb-safe-bottom' : ''}`}>
                <Routes>
                  <Route path="/" element={<RootRedirect />} />
                  <Route path="/home" element={hidePaywallUI() ? <Navigate to="/auth" replace /> : <Landing />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/signup" element={hidePaywallUI() ? <Navigate to="/auth" replace /> : <Signup />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/delete-account" element={<DeleteAccount />} />
                  <Route path="/help" element={<Help />} />
                  <Route path="/dashboard" element={<Index />} />
                  
                  <Route path="/account/profile" element={<Profile />} />
                  <Route path="/account/payment-settings" element={<PaymentSettings />} />
                  <Route path="/account/review-links" element={<ReviewLinks />} />
                  <Route path="/account/subscription" element={<Subscription />} />

                  <Route path="/customers" element={<Customers />} />
                  
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </main>
            </div>
          </BrowserRouter>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
    </>
  );
};

export default App;
